import { useEffect } from "react";

import { ErrorPage } from "@ezmid/components-library";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useRoutes } from "react-router";

import { makeRoute } from "../../hooks/route";
import { LoginView } from "../auth/LoginView";
import ResetPasswordView from "../auth/ResetPasswordView";
import SetNewPasswordView from "../auth/SetNewPasswordView";
import { CourseAddPage } from "../courses/pages/CourseAddPage";
import { CourseDetailPage } from "../courses/pages/CourseDetailPage";
import { CoursesListPage } from "../courses/pages/CoursesListPage";
import { DashboardPage } from "../dashboard/pages/DashboardPage";
import { PrivateContent } from "../layout/PrivateContent";
import { LecturerAddPage } from "../lecturers/pages/LecturerAddPage";
import { LecturerDetailPage } from "../lecturers/pages/LecturerDetailPage";
import { LecturersListPage } from "../lecturers/pages/LecturersListPage";
import { LessonDetailPage } from "../lessons/pages/LessonDetailPage";
import { PaymentsDetailPage } from "../payments/pages/PaymentsDetailPage";
import { PaymentsListPage } from "../payments/pages/PaymentsListPage";
import { ProductsPage } from "../products/ProductsPage";
import { AllRoutes } from "../route/Route";
import { SubscriptionPackageDetailPage } from "../subscriptions/pages/SubscriptionPackageDetailPage";
import { SubscriptionsListPage } from "../subscriptions/pages/SubscriptionsListPage";
import { UserDetailPage } from "../users/pages/UserDetailPage";
import { UserSettingsPage } from "../users/pages/UserSettingsPage";
import { UsersListPage } from "../users/pages/UsersListPage";

export function ApplicationRouter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(
    function redirectOnDashboardFromAdminURL() {
      if (location.pathname === "/admin") {
        navigate("/admin/dashboard");
      }
    },
    [location.pathname, navigate]
  );

  const routes = useRoutes([
    {
      index: true,
      path: "/",
      element: <Navigate to="/admin/dashboard" />,
    },
    {
      path: "/auth/login",
      element: <LoginView />,
    },
    {
      path: "/auth/reset-password/:token",
      element: <SetNewPasswordView />,
    },
    {
      path: "/auth/reset-password",
      element: <ResetPasswordView />,
    },
    {
      path: "admin",
      element: <PrivateContent />,
      children: [
        {
          path: "dashboard",
          element: <DashboardPage />,
        },
        {
          path: "users/list",
          element: <UsersListPage />,
        },
        {
          path: "users/:id",
          element: <UserDetailPage />,
        },
        {
          path: "settings",
          element: <UserSettingsPage />,
        },
        {
          path: "courses/list",
          element: <CoursesListPage />,
        },
        {
          path: "courses/add",
          element: <CourseAddPage />,
        },
        {
          path: "products/list",
          element: <ProductsPage />,
        },
        {
          path: "lecturers/list",
          element: <LecturersListPage />,
        },
        {
          path: "lecturers/:id",
          element: <LecturerDetailPage />,
        },
        {
          path: "lecturers/add",
          element: <LecturerAddPage />,
        },
        {
          path: "courses/:id",
          element: <CourseDetailPage />,
        },
        {
          path: "lessons/:id",
          element: <LessonDetailPage />,
        },
        {
          path: "subscriptions/list",
          element: <SubscriptionsListPage />,
        },
        {
          path: "subscriptions/:id",
          element: <SubscriptionPackageDetailPage />,
        },
        {
          path: "payments/list",
          element: <PaymentsListPage />,
        },
        {
          path: "payments/:id",
          element: <PaymentsDetailPage />,
        },
        {
          path: "*",
          element: (
            <ErrorPage
              errorMessageText={t("app.page.error.defaultMessage")}
              messageForUser={t("app.page.error.ups")}
              hasGoBackButton
              buttonText={t("app.goToDasboard")}
              onButtonClick={() => navigate(makeRoute(AllRoutes.DASHBOARD))}
            />
          ),
        },
      ],
    },
    {
      path: "*",
      element: (
        <ErrorPage
          errorMessageText={t("app.page.error.defaultMessage")}
          messageForUser={t("app.page.error.ups")}
          hasGoBackButton
          buttonText={t("app.goToDasboard")}
          onButtonClick={() => navigate(makeRoute(AllRoutes.DASHBOARD))}
        />
      ),
    },
  ]);

  return routes;
}
